import React, { useEffect, useState } from 'react';
import { Plus, X } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import ConfirmDialog from '../components/ConfirmDialog';

const statusColors = { ACTIVE: 'bg-blue-100 text-blue-700', CONFIRMED: 'bg-green-100 text-green-700', CANCELLED: 'bg-red-100 text-red-700', CONVERTED: 'bg-gray-100 text-gray-600' };

export default function Reservations() {
  const [reservations, setReservations] = useState([]);
  const [students, setStudents] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(null);
  const [form, setForm] = useState({ studentId: '', roomId: '', reservationFee: '', expectedMoveIn: '', notes: '' });

  const load = () => api.get('/reservations').then(r => setReservations(r.data)).catch(console.error);

  useEffect(() => {
    load();
    api.get('/students').then(r => setStudents(r.data));
    api.get('/rooms').then(r => setRooms(r.data.filter(r => r.status !== 'OCCUPIED')));
  }, []);

  const submit = async e => {
    e.preventDefault();
    try {
      await api.post('/reservations', form);
      toast.success('Reservation created');
      setShowForm(false);
      setForm({ studentId: '', roomId: '', reservationFee: '', expectedMoveIn: '', notes: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };

  const cancel = async () => {
    try {
      await api.put(`/reservations/${confirmCancel.id}/cancel`);
      toast.success('Reservation cancelled');
      setConfirmCancel(null);
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); setConfirmCancel(null); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-800">Reservations</h2>
        <button onClick={() => setShowForm(true)} className="bg-purple-700 hover:bg-purple-800 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2">
          <Plus size={16} /> New Reservation
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
              <tr>
                <th className="px-4 py-3 text-left">Student</th>
                <th className="px-4 py-3 text-left">Room</th>
                <th className="px-4 py-3 text-left">Fee</th>
                <th className="px-4 py-3 text-left">Expected Move-In</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {reservations.map(r => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3"><p className="font-medium">{r.student.firstName} {r.student.lastName}</p><p className="text-xs text-gray-400">{r.student.registrationNo}</p></td>
                  <td className="px-4 py-3">{r.room.roomNumber}</td>
                  <td className="px-4 py-3 font-semibold text-purple-700">Rs. {Number(r.reservationFee).toLocaleString()}</td>
                  <td className="px-4 py-3 text-gray-500">{r.expectedMoveIn ? new Date(r.expectedMoveIn).toLocaleDateString() : '-'}</td>
                  <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[r.status]}`}>{r.status}</span></td>
                  <td className="px-4 py-3">{r.status === 'ACTIVE' && <button onClick={() => setConfirmCancel({ id: r.id, name: `${r.student.firstName} ${r.student.lastName}`, room: r.room.roomNumber })} className="text-red-500 hover:text-red-700"><X size={16} /></button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {reservations.length === 0 && <p className="text-center text-gray-400 py-10">No reservations</p>}
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="font-bold text-lg mb-4">New Reservation</h3>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-700">Student *</label>
                <select required value={form.studentId} onChange={e => setForm(p => ({ ...p, studentId: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500">
                  <option value="">Select student</option>
                  {students.map(s => <option key={s.id} value={s.id}>{s.firstName} {s.lastName} ({s.registrationNo})</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Room *</label>
                <select required value={form.roomId} onChange={e => setForm(p => ({ ...p, roomId: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500">
                  <option value="">Select room</option>
                  {rooms.map(r => <option key={r.id} value={r.id}>{r.roomNumber} - {r.category} ({r.status})</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Reservation Fee (Rs.) *</label>
                <input type="number" required value={form.reservationFee} onChange={e => setForm(p => ({ ...p, reservationFee: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Expected Move-In</label>
                <input type="date" value={form.expectedMoveIn} onChange={e => setForm(p => ({ ...p, expectedMoveIn: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Notes</label>
                <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} rows={2}
                  className="w-full border rounded-lg px-3 py-2 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm">Cancel</button>
                <button type="submit" className="flex-1 bg-purple-700 text-white py-2 rounded-lg text-sm">Create</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!confirmCancel}
        title="Cancel Reservation"
        message={confirmCancel ? `Are you sure you want to cancel the reservation for ${confirmCancel.name} in Room ${confirmCancel.room}?` : ''}
        confirmLabel="Yes, Cancel"
        onConfirm={cancel}
        onCancel={() => setConfirmCancel(null)}
      />
    </div>
  );
}
